// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * The same shape, a measured distance away from itself.
 *
 * Half the questions a logistics plan exists to answer are about clearance,
 * and every one of them is this drawing operation:
 *
 *   — the buildable area is the lot line brought in thirty feet
 *   — the crane cannot pick outside its radius, and must not swing over the
 *     neighbour inside it
 *   — the pump truck reaches so far from where it can park
 *   — geothermal bores have to stand a minimum distance off each other and
 *     off the foundation
 *   — a temporary service has a maximum run from the meter
 *
 * Drawing them by eye is how a crane gets ordered that cannot reach the far
 * corner. Drawn at a stated distance, the sheet answers the question instead
 * of raising it.
 *
 * What this is not: a true polygon buffer. A real buffer rounds its outside
 * corners and resolves the self-intersections an inward offset creates, and
 * is a substantial piece of computational geometry. This mitres the corners
 * and refuses the cases it cannot do honestly, which is the right trade for a
 * drawing somebody reads rather than a dataset somebody computes against.
 */

export interface Pt { x: number; y: number }

/** Twice the signed area. The sign is the winding, which is what we need. */
export function signedArea2(points: Pt[]): number {
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i], q = points[(i + 1) % points.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a;
}

const good = (p: Pt | undefined | null): p is Pt =>
  !!p && Number.isFinite(p.x) && Number.isFinite(p.y);

/**
 * The path moved sideways by `distance`.
 *
 * On a closed shape a positive distance is outward and a negative one inward,
 * whichever way round the vertices happen to have been drawn — nobody thinks
 * of a setback as "negative if I drew the lot clockwise".
 *
 * On an open line there is no inside, so positive is to the left of the
 * direction it was drawn and negative to the right.
 *
 * Null when there is nothing honest to return: fewer than two points, a
 * distance that is not a distance, or an inward offset that has eaten the
 * shape — a forty-foot setback on a sixty-foot lot does not produce a thin
 * sliver, it produces a shape turned inside out, and drawing that as if it
 * were buildable area is worse than drawing nothing.
 */
export function offsetPath(
  points: Pt[] | null | undefined,
  distance: number,
  closed: boolean,
  miterLimit = 4,
): Pt[] | null {
  const pts = (points || []).filter(good);
  if (pts.length < 2) return null;
  if (!Number.isFinite(distance)) return null;
  if (distance === 0) return pts.map((p) => ({ ...p }));

  // Vertices repeated on top of each other have no direction and would make
  // the normal undefined, so they are dropped before anything is measured.
  const clean: Pt[] = [pts[0]];
  for (const p of pts.slice(1)) {
    const last = clean[clean.length - 1];
    if (Math.hypot(p.x - last.x, p.y - last.y) > 1e-9) clean.push(p);
  }
  // A closed ring whose last point repeats the first is the same ring.
  if (closed && clean.length > 2) {
    const a = clean[0], b = clean[clean.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) <= 1e-9) clean.pop();
  }
  if (clean.length < 2) return null;
  if (closed && clean.length < 3) return null;

  // Outward is outward whichever way the shape was drawn. Which way the
  // segment normal points depends on the winding: on a ring whose signed area
  // is positive, the left-hand normal points into the shape, so the sign has
  // to be flipped for "positive means outward" to hold.
  const orient = closed ? (signedArea2(clean) >= 0 ? -1 : 1) : 1;
  const d = distance * orient;

  const n = clean.length;
  const segs = closed ? n : n - 1;
  const normals: Pt[] = [];
  for (let i = 0; i < segs; i++) {
    const p = clean[i], q = clean[(i + 1) % n];
    const dx = q.x - p.x, dy = q.y - p.y;
    const len = Math.hypot(dx, dy);
    normals.push({ x: -dy / len, y: dx / len });
  }

  const out: Pt[] = clean.map((p, i) => {
    // An end of an open line takes its own segment's normal; every corner
    // takes the mitre between the two that meet there, pushed out by 1/cos of
    // the half angle so the two offset runs actually meet at a point.
    const a = closed ? normals[(i - 1 + segs) % segs] : normals[Math.max(0, i - 1)];
    const b = closed ? normals[i % segs] : normals[Math.min(segs - 1, i)];
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const mag = Math.hypot(mx, my);
    if (mag < 1e-9) return { x: p.x + a.x * d, y: p.y + a.y * d };
    // Capped: a hairpin's mitre runs away to infinity, and a spike across the
    // sheet is not a clearance line.
    const reach = Math.min(miterLimit, 1 / mag);
    return { x: p.x + (mx / mag) * d * reach, y: p.y + (my / mag) * d * reach };
  });

  // Has the offset eaten the shape?
  //
  // Comparing areas does not answer it: a square brought in by more than half
  // its width comes back as a smaller square, the right way round, with a
  // perfectly plausible area -- it has folded through itself. What gives it
  // away is a run that now points the other way. A 60' setback on a 100' lot
  // turns the north line around to face south, and that is not a thin strip
  // of buildable land, it is nothing at all.
  for (let i = 0; i < segs; i++) {
    const p = clean[i], q = clean[(i + 1) % n];
    const a = out[i], b = out[(i + 1) % n];
    const was = Math.hypot(q.x - p.x, q.y - p.y);
    const now = Math.hypot(b.x - a.x, b.y - a.y);
    const dot = (q.x - p.x) * (b.x - a.x) + (q.y - p.y) * (b.y - a.y);
    if (dot <= 0) return null;
    // Or collapsed onto itself: a 50' setback on a 100' lot meets in the
    // middle, and what comes back is a square a fraction of a foot across
    // that only arithmetic can see.
    if (now < was * 1e-9) return null;
  }
  return out;
}

/** A ring of `count` points at `radius` about a centre, for a swing or a reach. */
export function ringAbout(centre: Pt, radius: number, count = 48): Pt[] | null {
  if (!good(centre)) return null;
  if (!Number.isFinite(radius) || radius <= 0) return null;
  const n = Math.max(8, Math.min(180, Math.round(count)));
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    out.push({ x: centre.x + Math.cos(t) * radius, y: centre.y + Math.sin(t) * radius });
  }
  return out;
}

/** What to call the shape this produced, given what it came from. */
export function offsetLabel(sourceLabel: string | null | undefined, feet: number): string {
  const name = String(sourceLabel ?? "").trim();
  const dist = Math.abs(feet);
  const pretty = Number.isInteger(dist) ? String(dist) : dist.toFixed(1);
  const side = feet < 0 ? "inside" : "outside";
  return name ? `${pretty}' ${side} ${name}` : `${pretty}' offset`;
}
